<template>
  <span>
    <v-menu offset-y left v-model="isMenuOpen" max-height="600px" max-width="450px" :close-on-content-click="false">
      <template v-slot:activator="{ on }">
        <v-btn icon v-on="on" @click="notificationOpened()">
          <v-badge :color="unreadNotifications.length > 0 ? 'blue' : ''" overlap :content="unreadNotifications.length">
            <v-icon>mdi-bell</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <v-skeleton-loader
        v-if="notificationsRequest.loading"
        ref="skeleton"
        type="list-item-avatar-three-line@5"
        class="mx-auto"
      ></v-skeleton-loader>
      <v-list v-if="!notificationsRequest.loading && isMenuOpen" dense subheader>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>{{ $lang('main.navbar.notifications.title') }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  v-on="on"
                  @click="readAll"
                >
                  <v-icon color="primary">mdi-email-open</v-icon>
                </v-btn>
              </template>
              <span>{{ $lang('main.navbar.notifications.read_all') }}</span>
            </v-tooltip>
          </v-list-item-action>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item v-if="!notifications.length">
          <v-list-item-title>{{$lang('main.crud.no_records')}}</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-for="(notification, r) in notifications"
          :key="r"
          two-line
          @click="goToItem(notification)"
        >
          <v-list-item-avatar>
            <img v-if="!!notification.data.image" :src="notification.data.image" />
            <img v-else src="/img/icon.svg" />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>
              {{ notification.data.text }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $lang('main.navbar.notifications.sent') }}
              <span v-if="!!notification.created_at">
                {{ $fromNow(notification.created_at) }}
              </span>
              <span v-else>
                {{ $fromNow($moment().tz('UTC').format('YYYY-MM-DD HH:mm:ss')) }}
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn v-if="!!notification.read_at" @click="unread(notification)" icon>
              <v-icon color="grey lighten-1">mdi-email-open</v-icon>
            </v-btn>
            <v-btn v-else @click="read(notification)" icon>
              <v-icon color="blue-grey">mdi-email</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-dialog v-model="isOpenReservationEditing" persistent fullscreen hide-overlay>
      <ReservationEditingForm
        v-if="isOpenReservationEditing"
        :item-id="activeReservationId"
        :open-tab="tabToOpen"
        @cancel="closeReservationEditing"
      />
    </v-dialog>
  </span>
</template>

<script>
  import { mapState } from "vuex";
  import MixpanelEvents from "@/constants/events";
  import {
    GET_AUTH_USER_NOTIFICATIONS,
    READ_ALL_AUTH_USER_NOTIFICATIONS,
    BROADCAST_AUTH_USER_NOTIFICATIONS,
    READ_AUTH_USER_NOTIFICATIONS,
    UNREAD_AUTH_USER_NOTIFICATIONS
  } from "@/store/auth/actions";

  const ReservationEditingForm = () => import("@/pages/reservations/EditingForm");

  export default {
    name: "Notifications",
    components: {
      ReservationEditingForm,
    },
    data() {
      return {
        isMenuOpen: false,
        isOpenReservationEditing: false,
        activeReservationId: null,
        tabToOpen: null,
      }
    },
    beforeMount() {
      this.getNotifications();
      this.broadcastNotifications();
    },
    methods: {
      readAll() {
        this.$monitorService.track(MixpanelEvents.NOTIFICATION_READ_ALL, {
          unread_notifications: this.unreadNotifications.length,
          notifications: this.notifications.length,
        });
        if (this.unreadNotifications.length === 0) {
          return;
        }

        this.$store.dispatch(READ_ALL_AUTH_USER_NOTIFICATIONS);
      },
      read(notification) {
        this.$store.dispatch(READ_AUTH_USER_NOTIFICATIONS, notification.id);
      },
      unread(notification) {
        this.$store.dispatch(UNREAD_AUTH_USER_NOTIFICATIONS, notification.id);
      },
      getNotifications() {
        this.$store.dispatch(GET_AUTH_USER_NOTIFICATIONS);
      },
      broadcastNotifications() {
        this.$store.dispatch(BROADCAST_AUTH_USER_NOTIFICATIONS)
      },
      goToItem(item) {
        this.read(item);
        if (!item.data || !item.data.type) return;

        this.$monitorService.track(MixpanelEvents.NOTIFICATION_ITEM_CLICKED, {
          item_type: item.data.type || 'ND',
        });

        const isOpenReservation = [
          'reservation_created',
          'reservation_updated',
          'reservation_cancelled'
        ].includes(item.data.type);
        const isMessageNotification = item.data.type === 'guest_message_received';
        const isReservationListRoute = false;

        if (isOpenReservation) {
          this.activeReservationId = item.data.reservation.id;
          this.isOpenReservationEditing = true;
        } else if (isMessageNotification) {
          this.activeReservationId = item.data.reservation.id;
          this.tabToOpen = 'tab-messaging';
          this.isOpenReservationEditing = true;
        } else if (isReservationListRoute) {
          this.$router.push({
            name: 'reservations',
            query: { res_id: item.data.reservation.res_id }
          });
        } else if (item.data.ticket_id) {
          this.$router.push({
            name: 'ticket-messages',
            params: { id: item.data.ticket_id }
          });
        }
      },
      notificationOpened() {
        this.$monitorService.track(MixpanelEvents.NOTIFICATION_BADGE_CLICKED);
      },
      closeReservationEditing() {
        this.isOpenReservationEditing = false;
      }
    },
    computed: {
      unreadNotifications() {
        return this.notifications.filter(notification => !notification.read_at);
      },
      ...mapState({
        notifications: state => state.auth.getNotificationsRequest.notifications,
        notificationsRequest: state => state.auth.getNotificationsRequest,
        readAllRequest: state => state.auth.readAllNotificationsRequest,
      }),
    }
  };
</script>

<style scoped></style>
